import * as React from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  Tooltip,
} from "@mui/material";
import { LoadingProgress } from "fe-web-components/loader/loadingProgress";
import { useCallback, useMemo, useState } from "react";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as DemoIcon } from "../../assets/clinic/demo.svg";
import { ClinicColors } from "fe-web-components/styles/clinicTheme";
import { ClinicWithFavorite } from "./clinicManage";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";

//region [[ Styles ]]

const ClinicSelectionDialogView = styled(Dialog)({});
const DialogHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  padding: "0 20px",
  gap: 10,
  "& .search-box": {
    marginLeft: "auto",
  },
});
const ClinicItem = styled("div")({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  padding: "0 10px",
  borderRadius: 4,
  "&:hover, &.selected-clinic": {
    backgroundColor: ClinicColors.tertiary,
  },
  "& .demo-clinic": {
    fill: ClinicColors.secondary,
    marginLeft: 10,
  },
});
const FilterButton = styled(IconButton)({
  "&.active": {
    color: ClinicColors.primary,
    backgroundColor: ClinicColors.mainBackground,
  },
  "& svg": {
    fill: "currentcolor",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface ClinicSelectionDialogProps {
  userClinics: ClinicWithFavorite[];
  goToClinic: (clinicId: string) => void;
  currentClinicId: string;
  open: boolean;
  onClose: () => void;
  onToggleUserClinic?: (clinic: ClinicWithFavorite) => Promise<boolean>;
  loading?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ClinicSelectionDialog = ({
  ...props
}: ClinicSelectionDialogProps) => {
  const [showOnlyFavs, setShowOnlyFavs] = useState(false);
  const [showOnlyDemos, setShowOnlyDemos] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState(null);

  const toggleUserClinic = (clinic: ClinicWithFavorite) => {
    setUpdating(true);
    props.onToggleUserClinic(clinic).then(() => setUpdating(false));
  };

  const { userClinics, currentClinicId, loading } = props;

  const goToClinic = (clinicId: string) => {
    if (currentClinicId === clinicId) handleClose();
    else props.goToClinic(clinicId);
  };

  const handleSearchTermChanged = useCallback(
    (e) => {
      e.stopPropagation();
      if (e?.target?.value === "") setSearchTerm(null);
      else setSearchTerm(e?.target?.value);
    },
    [setSearchTerm],
  );

  const handleClose = () => {
    props.onClose();
  };

  const isDemo = (c: ClinicWithFavorite) => {
    return c.config[ClinicConfigKeys.UnifiedConfigString]?.["clinic.demoMode"];
  };

  const filtered = useMemo(() => {
    if (!userClinics) return [];
    let clinics = !showOnlyFavs
      ? userClinics
      : userClinics.filter((c) => c.isFavorite);
    if (showOnlyDemos) {
      clinics = clinics.filter(isDemo);
    }
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      clinics = clinics.filter(
        (c) =>
          c.name?.toLowerCase().includes(lowerSearchTerm) ||
          c.displayName?.toLowerCase().includes(lowerSearchTerm),
      );
    }
    return clinics.sort((a, b) =>
      (a.displayName || a?.name)?.localeCompare(b?.displayName || b?.name),
    );
  }, [searchTerm, showOnlyFavs, showOnlyDemos, userClinics]);

  return (
    <ClinicSelectionDialogView
      onClose={handleClose}
      open={props.open}
      maxWidth="sm"
    >
      <DialogHeader>
        <h2>Select Clinic</h2>
        <TextField
          className="search-box"
          variant="outlined"
          placeholder="Search"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          value={searchTerm || ""}
          onChange={handleSearchTermChanged}
          disabled={loading || updating}
        />
        <Tooltip title={"Show only favorite clinics"}>
          <FilterButton
            onClick={() => setShowOnlyFavs((old) => !old)}
            disabled={loading || updating}
            className={showOnlyFavs ? "active" : ""}
          >
            {showOnlyFavs ? <StarIcon /> : <StarBorderIcon />}
          </FilterButton>
        </Tooltip>
        <Tooltip title={"Show only demo clinics"}>
          <FilterButton
            onClick={() => setShowOnlyDemos((old) => !old)}
            disabled={loading || updating}
            className={showOnlyDemos ? "active" : ""}
          >
            <DemoIcon />
          </FilterButton>
        </Tooltip>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogHeader>
      <DialogContent style={{ minWidth: "600px", minHeight: "280px" }}>
        <>
          {loading && <LoadingProgress label="Loading clinic list" />}
          {!loading &&
            filtered?.map((clinic) => (
              <ClinicItem
                key={clinic.id}
                onClick={() => goToClinic(clinic.id)}
                className={
                  clinic.id === currentClinicId ? "selected-clinic" : ""
                }
              >
                <div>{clinic.displayName || clinic.name}</div>
                {isDemo(clinic) && <DemoIcon className={"demo-clinic"} />}
                <Tooltip
                  title={
                    clinic.isFavorite
                      ? "Remove from favorite"
                      : "Add to favorite"
                  }
                >
                  {props.onToggleUserClinic && (
                    <IconButton
                      disabled={loading}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleUserClinic(clinic);
                      }}
                    >
                      {clinic.isFavorite ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                  )}
                </Tooltip>
              </ClinicItem>
            ))}
          {!loading && !filtered.length && <div>No clinics found</div>}
        </>
      </DialogContent>
    </ClinicSelectionDialogView>
  );
};
